import { useEffect } from 'react';

import { useLocation } from '@remix-run/react';

import { usePixelLoader } from '~/hooks/usePixelLoader';

import { markMetaPixelReady, validMetaPixelId, whenMetaPixelReady } from './metaPixel';

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
}

const FacebookPixel = ({ pixelId, marketingCookies }: PixelProps) => {
  const location = useLocation();
  const trackedPath = `${location.pathname}${location.search}`;
  const resolvedPixelId = validMetaPixelId(pixelId);

  // The pixel snippet loads on idle, so PageView waits until this pixel is
  // initialized. One event per path, including the first page.
  useEffect(() => {
    if (!resolvedPixelId || !marketingCookies || !trackedPath) return;

    let sent = false;
    return whenMetaPixelReady(resolvedPixelId, () => {
      if (sent || typeof window === 'undefined' || !window.fbq) return;
      sent = true;
      window.fbq('trackSingle', resolvedPixelId, 'PageView');
    });
  }, [trackedPath, resolvedPixelId, marketingCookies]);

  usePixelLoader(() => {
    if (!resolvedPixelId || !marketingCookies) {
      return;
    }

    // Prevent duplicate script injection. The pixel is already initialized.
    if (document.getElementById('facebook-pixel')) {
      markMetaPixelReady(resolvedPixelId);
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-pixel';
    script.async = true;
    script.innerHTML = `
      (function(){try{
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', "${resolvedPixelId}");
      }catch{/*swallow exception*/}})();
    `;
    document.head.appendChild(script);
    markMetaPixelReady(resolvedPixelId);

    return () => {
      try {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [resolvedPixelId, marketingCookies]);

  return null;
};

export default FacebookPixel;
